import React, { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import { useAccessControl } from "../context/useAccessControl";
import {
  buildPublicQrUrl,
  createBranchTablesWithQrs,
  deleteBranchTableWithQr,
  disableBranchTableQrAccess,
  enableBranchTableQrAccess,
  enableBranchTablesQrAccess,
  getBranchQrAccessDurationMinutes,
  listBranchTablesWithQrs,
  updateBranchQrAccessDurationMinutes,
} from "../../services/supabase/qrService";
import {
  getOperationalOrdersState,
  saveOperationalOrdersState,
} from "../../services/local/operationalOrders";
import {
  getWaiterOperationsState,
  saveWaiterOperationsState,
} from "../../services/local/waiterOperations";
import {
  getPaymentOperations,
  savePaymentOperations,
} from "../../services/local/paymentOperations";
import { saveBranchTablesSnapshot } from "../../services/local/branchTablesSnapshot";
import QrSalonAccessPanel from "../common/QrSalonAccessPanel";

const formatQrAccessUntil = (value) => {
  if (!value) {
    return "";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isQrAccessEnabled = (mesa) => {
  const expiresAt = Date.parse(mesa?.qr?.accessEnabledUntil || "");
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
};

const DEFAULT_QR_ACCESS_DURATION_HOURS = 2;

const normalizeQrAccessHours = (value) =>
  Math.min(
    12,
    Math.max(
      1,
      Number(value || DEFAULT_QR_ACCESS_DURATION_HOURS) ||
        DEFAULT_QR_ACCESS_DURATION_HOURS
    )
  );

const pxToMm = (px) => Number(((Number(px) || 0) * 0.264583).toFixed(2));

function QrMeza() {
  const {
    canAccessModule,
    canPerformAction,
    currentAccessRoleLabel,
    currentBranch,
    currentRestaurant,
    isCreatorMode,
    isSupabaseConfigured,
  } = useAccessControl();
  const [cantidad, setCantidad] = useState("");
  const [tamanio, setTamanio] = useState(128);
  const [mesas, setMesas] = useState([]);
  const [loadingMesas, setLoadingMesas] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [enablingAllTables, setEnablingAllTables] = useState(false);
  const [loadingQrDuration, setLoadingQrDuration] = useState(true);
  const [qrAccessHours, setQrAccessHours] = useState(
    DEFAULT_QR_ACCESS_DURATION_HOURS
  );
  const [message, setMessage] = useState({ type: "", text: "" });
  const qrRefs = useRef({});
  const canSeeQrModule = canAccessModule("qr");
  const canManageQr = canSeeQrModule && canPerformAction("manage_qr");

  const nombreSucursal = currentBranch?.name || currentRestaurant?.name || "Sucursal";
  const tamanioPdfMm = pxToMm(tamanio);

  const notifyQrPermissionDenied = (actionLabel) => {
    setMessage({
      type: "error",
      text: `${currentAccessRoleLabel} puede consultar los QR de la sucursal, pero no ${actionLabel}.`,
    });
  };

  useEffect(() => {
    const loadQrAccessDuration = async () => {
      if (!currentBranch?.id || isCreatorMode || !isSupabaseConfigured) {
        setQrAccessHours(DEFAULT_QR_ACCESS_DURATION_HOURS);
        setLoadingQrDuration(false);
        return;
      }

      setLoadingQrDuration(true);

      try {
        const durationMinutes = await getBranchQrAccessDurationMinutes(currentBranch.id);
        setQrAccessHours(normalizeQrAccessHours(durationMinutes / 60));
      } catch (error) {
        setMessage({
          type: "error",
          text: error.message || "No se pudo cargar la duracion por defecto del QR.",
        });
      } finally {
        setLoadingQrDuration(false);
      }
    };

    loadQrAccessDuration();
  }, [currentBranch?.id, isCreatorMode, isSupabaseConfigured]);

  useEffect(() => {
    const loadTables = async () => {
      if (!currentBranch?.id || isCreatorMode || !isSupabaseConfigured) {
        setMesas([]);
        setLoadingMesas(false);
        return;
      }

      setLoadingMesas(true);

      try {
        const nextTables = await listBranchTablesWithQrs(currentBranch.id);
        setMesas(nextTables);
        saveBranchTablesSnapshot(currentBranch.id, nextTables);
        setMessage({ type: "", text: "" });
      } catch (error) {
        setMessage({
          type: "error",
          text: error.message || "No se pudieron cargar las mesas con QR.",
        });
      } finally {
        setLoadingMesas(false);
      }
    };

    loadTables();
  }, [currentBranch?.id, isCreatorMode, isSupabaseConfigured]);

  const mesasConQr = useMemo(
    () => mesas.filter((mesa) => mesa.qr?.token),
    [mesas]
  );

  const generarQrs = async () => {
    if (!canManageQr) {
      notifyQrPermissionDenied("generar nuevas mesas QR");
      return;
    }

    const cantidadNumerica = parseInt(cantidad, 10);

    if (!cantidadNumerica || cantidadNumerica < 1) {
      setMessage({
        type: "error",
        text: "Ingresa una cantidad valida de mesas para generar QR.",
      });
      return;
    }

    if (!currentBranch?.id) {
      setMessage({
        type: "error",
        text: "No encontramos una sucursal activa para crear los QR.",
      });
      return;
    }

    setProcessing(true);

    try {
      const nextTables = await createBranchTablesWithQrs(
        currentBranch.id,
        cantidadNumerica,
        Number(tamanio)
      );
      setMesas(nextTables);
      saveBranchTablesSnapshot(currentBranch.id, nextTables);
      setCantidad("");
      setMessage({
        type: "success",
        text: `Se generaron ${cantidadNumerica} nuevas mesas con su QR.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudieron generar los QR de las mesas.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const imprimirQR = async (mesa) => {
    if (!canManageQr) {
      notifyQrPermissionDenied("imprimir QR individuales");
      return;
    }

    setProcessing(true);

    try {
      const qrCanvas = qrRefs.current[mesa.id]?.querySelector("canvas");
      if (!qrCanvas) {
        throw new Error("No encontramos el QR de la mesa seleccionada.");
      }

      const imgData = qrCanvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const qrWidth = tamanioPdfMm;
      const qrHeight = qrWidth;
      const x = (pageWidth - qrWidth) / 2;
      const y = 34;

      pdf.setFontSize(16);
      pdf.text(`${nombreSucursal} - ${mesa.label || `Mesa ${mesa.tableNumber}`}`, pageWidth / 2, 20, {
        align: "center",
      });
      pdf.addImage(imgData, "PNG", x, y, qrWidth, qrHeight);
      pdf.save(`QR-${nombreSucursal}-mesa-${mesa.tableNumber}.pdf`);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudo generar el PDF de la mesa.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const imprimirTodosQR = async () => {
    if (!canManageQr) {
      notifyQrPermissionDenied("imprimir el lote completo de QR");
      return;
    }

    if (!mesasConQr.length) {
      return;
    }

    setProcessing(true);

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const spacing = 10;
      const headerHeight = 10;
      let y = margin;
      let itemsPerPage = 0;

      for (let index = 0; index < mesasConQr.length; index += 1) {
        const mesa = mesasConQr[index];
        const qrCanvas = qrRefs.current[mesa.id]?.querySelector("canvas");

        if (!qrCanvas) {
          continue;
        }

        const qrWidth = tamanioPdfMm;
        const qrHeight = qrWidth;
        const maxItemsPerPage = Math.max(
          1,
          Math.floor((pageHeight - margin * 2) / (qrHeight + spacing + headerHeight))
        );

        if (itemsPerPage >= maxItemsPerPage) {
          pdf.addPage();
          y = margin;
          itemsPerPage = 0;
        }

        const imgData = qrCanvas.toDataURL("image/png");
        const x = (pageWidth - qrWidth) / 2;

        pdf.setFontSize(16);
        pdf.text(
          `${nombreSucursal} - ${mesa.label || `Mesa ${mesa.tableNumber}`}`,
          pageWidth / 2,
          y,
          { align: "center" }
        );
        y += headerHeight;

        pdf.addImage(imgData, "PNG", x, y, qrWidth, qrHeight);
        y += qrHeight + spacing;
        itemsPerPage += 1;
      }

      pdf.save(`QR-${nombreSucursal}.pdf`);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudieron imprimir todos los QR.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const copiarLink = async (mesa) => {
    if (!canManageQr) {
      notifyQrPermissionDenied("copiar links publicos");
      return;
    }

    try {
      await navigator.clipboard.writeText(buildPublicQrUrl(mesa.qr.token));
      setMessage({
        type: "success",
        text: `Link copiado para ${mesa.label || `Mesa ${mesa.tableNumber}`}.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: "No se pudo copiar el link del QR.",
      });
    }
  };

  const updateMesasQrAccessState = (qrPatches) => {
    const patchesByTableId = new Map(
      (Array.isArray(qrPatches) ? qrPatches : []).map((qrPatch) => [
        String(qrPatch?.tableId || ""),
        qrPatch,
      ])
    );

    setMesas((prev) => {
      const nextTables = prev.map((mesa) => {
        const nextQrPatch = patchesByTableId.get(String(mesa.id || ""));

        return nextQrPatch && mesa.qr
          ? {
              ...mesa,
              qr: {
                ...mesa.qr,
                ...nextQrPatch,
              },
            }
          : mesa;
      });

      if (currentBranch?.id) {
        saveBranchTablesSnapshot(currentBranch.id, nextTables);
      }

      return nextTables;
    });
  };

  const updateMesaQrAccessState = (mesaId, nextQrPatch) => {
    updateMesasQrAccessState([
      {
        ...nextQrPatch,
        tableId: mesaId,
      },
    ]);
  };

  const habilitarMesaQr = async (mesa) => {
    if (!canManageQr) {
      notifyQrPermissionDenied("habilitar accesos QR");
      return;
    }

    setProcessing(true);

    try {
      const updatedQr = await enableBranchTableQrAccess(mesa.id, qrAccessHours * 60);
      updateMesaQrAccessState(mesa.id, updatedQr);
      setMessage({
        type: "success",
        text: `${mesa.label || `Mesa ${mesa.tableNumber}`} quedo habilitada por ${qrAccessHours} hora${qrAccessHours === 1 ? "" : "s"} para pedidos QR.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudo habilitar el QR de la mesa.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const habilitarTodasLasMesasQr = async () => {
    if (!canManageQr) {
      notifyQrPermissionDenied("habilitar todos los accesos QR");
      return;
    }

    if (!currentBranch?.id) {
      setMessage({
        type: "error",
        text: "No encontramos la sucursal activa para habilitar sus mesas.",
      });
      return;
    }

    if (!mesasConQr.length) {
      setMessage({
        type: "error",
        text: "Todavia no hay mesas con QR para habilitar.",
      });
      return;
    }

    setProcessing(true);
    setEnablingAllTables(true);

    try {
      const updatedQrs = await enableBranchTablesQrAccess(
        currentBranch.id,
        qrAccessHours * 60
      );
      updateMesasQrAccessState(updatedQrs);
      setMessage({
        type: "success",
        text: `Las ${mesasConQr.length} mesas quedaron habilitadas por ${qrAccessHours} hora${qrAccessHours === 1 ? "" : "s"} para pedidos QR.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudieron habilitar todas las mesas.",
      });
    } finally {
      setEnablingAllTables(false);
      setProcessing(false);
    }
  };

  const guardarDuracionQr = async () => {
    if (!canManageQr) {
      notifyQrPermissionDenied("cambiar la duracion del acceso QR");
      return;
    }

    if (!currentBranch?.id) {
      setMessage({
        type: "error",
        text: "No encontramos la sucursal activa para guardar la duracion del QR.",
      });
      return;
    }

    setProcessing(true);

    try {
      const savedMinutes = await updateBranchQrAccessDurationMinutes(
        currentBranch.id,
        normalizeQrAccessHours(qrAccessHours) * 60
      );
      const nextHours = normalizeQrAccessHours(savedMinutes / 60);
      setQrAccessHours(nextHours);
      setMessage({
        type: "success",
        text: `La duracion por defecto del QR quedo configurada en ${nextHours} hora${nextHours === 1 ? "" : "s"}.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudo guardar la duracion por defecto del QR.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const cerrarAccesoMesaQr = async (mesa) => {
    if (!canManageQr) {
      notifyQrPermissionDenied("cerrar accesos QR");
      return;
    }

    setProcessing(true);

    try {
      const updatedQr = await disableBranchTableQrAccess(mesa.id);
      updateMesaQrAccessState(mesa.id, updatedQr);
      setMessage({
        type: "success",
        text: `Cerramos el acceso QR de ${mesa.label || `Mesa ${mesa.tableNumber}`}.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudo cerrar el acceso QR de la mesa.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const eliminarMesa = async (mesa) => {
    if (!canManageQr) {
      notifyQrPermissionDenied("eliminar mesas QR");
      return;
    }

    const confirmed = window.confirm(
      `Confirma que deseas eliminar ${mesa.label || `Mesa ${mesa.tableNumber}`}.`
    );

    if (!confirmed) {
      return;
    }

    const operationalState = getOperationalOrdersState(currentRestaurant);
    const hasActiveOrder = (operationalState.mesasActivas || []).some(
      (order) =>
        order.tableId === mesa.id &&
        order.phase !== "closed" &&
        order.phase !== "cancelled"
    );

    if (hasActiveOrder) {
      setMessage({
        type: "error",
        text: `No puedes eliminar ${mesa.label || `Mesa ${mesa.tableNumber}`} hasta cerrar o entregar su pedido activo.`,
      });
      return;
    }

    setProcessing(true);

    try {
      const removedOrderIds = new Set(
        (operationalState.mesasActivas || [])
          .filter((order) => order.tableId === mesa.id)
          .map((order) => order.id)
      );

      await deleteBranchTableWithQr(mesa.id);
      const nextTables = await listBranchTablesWithQrs(currentBranch.id);
      setMesas(nextTables);
      saveBranchTablesSnapshot(currentBranch.id, nextTables);

      saveOperationalOrdersState(currentRestaurant, {
        ...operationalState,
        mesasActivas: (operationalState.mesasActivas || []).filter(
          (order) => order.tableId !== mesa.id
        ),
        pedidosHistoricos: operationalState.pedidosHistoricos || [],
      });

      const waiterState = getWaiterOperationsState(currentRestaurant);
      saveWaiterOperationsState(currentRestaurant, {
        ...waiterState,
        tableAssignments: (waiterState.tableAssignments || []).filter(
          (assignment) => assignment.tableId !== mesa.id
        ),
        deliveryAlerts: (waiterState.deliveryAlerts || []).filter(
          (alert) => alert.tableId !== mesa.id && !removedOrderIds.has(alert.orderId)
        ),
        serviceRequests: (waiterState.serviceRequests || []).filter(
          (request) => request.tableId !== mesa.id
        ),
      });

      const paymentOperations = getPaymentOperations(currentRestaurant);
      savePaymentOperations(
        currentRestaurant,
        paymentOperations.filter(
          (operation) =>
            operation.tableId !== mesa.id &&
            !removedOrderIds.has(operation.orderId)
        )
      );

      setMessage({
        type: "success",
        text: `${mesa.label || `Mesa ${mesa.tableNumber}`} eliminada.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "No se pudo eliminar la mesa seleccionada.",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (isCreatorMode || !isSupabaseConfigured) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyState}>
          El modulo de QR por mesa necesita una sesion real con Supabase para crear mesas,
          guardar tokens y abrir la pagina publica del comensal.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <section style={styles.header}>
        <div style={styles.headerCopy}>
          <div style={styles.overline}>QR por mesa</div>
          <h1 style={styles.title}>{nombreSucursal}</h1>
          <p style={styles.subtitle}>
            Desde aqui generas los QR reales de cada mesa. El comensal escanea, entra a la
            pagina publica de su mesa y ve el menu correcto de esta sucursal.
          </p>
        </div>

        <div style={styles.headerTools}>
          <label style={styles.inputGroup}>
            <span style={styles.label}>Cantidad de mesas nuevas</span>
            <input
              type="number"
              min="1"
              value={cantidad}
              onChange={(event) => setCantidad(event.target.value)}
              style={styles.input}
              placeholder="Ej: 6"
              disabled={!canManageQr || processing}
            />
          </label>

          <button
            type="button"
            style={styles.primaryButton}
            onClick={generarQrs}
            disabled={!canManageQr || processing}
          >
            {processing ? "Procesando..." : "Generar QR"}
          </button>
        </div>
      </section>

      {!canManageQr ? (
        <div style={{ ...styles.notice, ...styles.noticeInfo }}>
          {currentAccessRoleLabel} puede revisar los QR ya creados, pero no generar, imprimir ni
          cambiar accesos de esta sucursal.
        </div>
      ) : null}

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>Tamano de impresion</h2>
            <p style={styles.panelText}>
              Ajusta el tamano de todos los QR al imprimirlos en PDF, incluidos los que ya creaste.
            </p>
          </div>
          {mesasConQr.length > 0 && (
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={imprimirTodosQR}
              disabled={!canManageQr || processing}
            >
              {processing ? "Generando..." : "Imprimir todos"}
            </button>
          )}
        </div>

        <div style={styles.sliderRow}>
          <input
            type="range"
            min="96"
            max="256"
            step="8"
            value={tamanio}
            onChange={(event) => setTamanio(Number(event.target.value))}
            aria-label="Tamano de impresion del QR"
            aria-valuetext={`${tamanioPdfMm} milimetros`}
            style={styles.slider}
            disabled={!canManageQr || processing}
          />
          <div style={styles.sliderMeta}>
            {tamanio}px / {tamanioPdfMm} mm
          </div>
        </div>
        <p style={styles.panelText}>
          Al imprimir el PDF, elige Tamano real o escala 100% para conservar esta medida.
        </p>
      </section>

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>Duracion del acceso QR</h2>
            <p style={styles.panelText}>
              Define cuantas horas queda abierta cada mesa al tocar habilitar o renovar. Por defecto queda en 2 horas.
            </p>
          </div>
          <div style={styles.panelActions}>
            <button
              type="button"
              style={styles.primaryButton}
              onClick={habilitarTodasLasMesasQr}
              disabled={
                !canManageQr ||
                processing ||
                loadingMesas ||
                loadingQrDuration ||
                mesasConQr.length === 0
              }
            >
              {enablingAllTables ? "Habilitando..." : "Habilitar todas las mesas"}
            </button>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={guardarDuracionQr}
              disabled={!canManageQr || processing || loadingQrDuration}
            >
              {processing && !enablingAllTables ? "Guardando..." : "Guardar duracion"}
            </button>
          </div>
        </div>

        <div style={styles.durationRow}>
          <label style={styles.inputGroup}>
            <span style={styles.label}>Horas por defecto</span>
            <input
              type="number"
              min="1"
              max="12"
              step="1"
              value={qrAccessHours}
              onChange={(event) => setQrAccessHours(normalizeQrAccessHours(event.target.value))}
              style={styles.input}
              disabled={!canManageQr || loadingQrDuration || processing}
            />
          </label>
          <div style={styles.durationHint}>
            {loadingQrDuration
              ? "Cargando configuracion..."
              : `Cada mesa se abrira por ${qrAccessHours} hora${qrAccessHours === 1 ? "" : "s"} cuando el admin la habilite.`}
          </div>
        </div>
      </section>

      <QrSalonAccessPanel
        branchId={currentBranch?.id}
        tables={mesasConQr}
        hours={qrAccessHours}
        canManage={canManageQr}
        disabled={processing || loadingMesas || loadingQrDuration}
        onUpdated={updateMesasQrAccessState}
        onBusy={setProcessing}
      />

      {message.text && (
        <div
          style={{
            ...styles.notice,
            ...(message.type === "error" ? styles.noticeError : styles.noticeSuccess),
          }}
        >
          {message.text}
        </div>
      )}

      {loadingMesas ? (
        <div style={styles.emptyState}>Estamos cargando las mesas de la sucursal activa.</div>
      ) : mesasConQr.length === 0 ? (
        <div style={styles.emptyState}>
          Todavia no hay mesas con QR generadas para esta sucursal.
        </div>
      ) : (
        <section style={styles.grid}>
          {mesasConQr.map((mesa) => {
            const publicUrl = buildPublicQrUrl(mesa.qr.token);

            return (
              <article key={mesa.id} style={styles.card}>
                <div ref={(node) => (qrRefs.current[mesa.id] = node)} style={styles.qrWrap}>
                  <QRCodeCanvas value={publicUrl} size={tamanio} level="H" style={{ maxWidth: "100%", height: "auto" }} />
                </div>

                <div style={styles.cardBody}>
                  <strong style={styles.cardTitle}>
                    {mesa.label || `Mesa ${mesa.tableNumber}`}
                  </strong>
                  <div
                    style={{
                      ...styles.accessBadge,
                      ...(isQrAccessEnabled(mesa)
                        ? styles.accessBadgeEnabled
                        : styles.accessBadgeDisabled),
                    }}
                  >
                    {isQrAccessEnabled(mesa)
                      ? `QR habilitado hasta ${formatQrAccessUntil(mesa.qr.accessEnabledUntil)}`
                      : "QR cerrado hasta nueva habilitacion"}
                  </div>
                  <div style={styles.cardMeta}>Token: {mesa.qr.token}</div>
                  <div style={styles.linkPreview}>{publicUrl}</div>
                </div>

                <div style={styles.actions}>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={styles.linkButton}
                  >
                    Abrir menu
                  </a>
                  <button
                    type="button"
                    style={styles.secondaryMiniButton}
                    onClick={() => copiarLink(mesa)}
                    disabled={!canManageQr || processing}
                  >
                    Copiar link
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryMiniButton}
                    onClick={() => habilitarMesaQr(mesa)}
                    disabled={!canManageQr || processing}
                  >
                    {isQrAccessEnabled(mesa)
                      ? `Renovar ${qrAccessHours}h`
                      : `Habilitar ${qrAccessHours}h`}
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryMiniButton}
                    onClick={() => cerrarAccesoMesaQr(mesa)}
                    disabled={!canManageQr || processing}
                  >
                    Cerrar acceso
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryMiniButton}
                    onClick={() => imprimirQR(mesa)}
                    disabled={!canManageQr || processing}
                  >
                    Imprimir
                  </button>
                  <button
                    type="button"
                    style={styles.dangerMiniButton}
                    onClick={() => eliminarMesa(mesa)}
                    disabled={!canManageQr || processing}
                  >
                    Eliminar
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: "22px",
    padding: "8px 6px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "18px",
    flexWrap: "wrap",
  },
  headerCopy: {
    display: "grid",
    gap: "10px",
    maxWidth: "760px",
  },
  overline: {
    color: "#2563eb",
    fontSize: "13px",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "32px",
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    lineHeight: 1.7,
  },
  headerTools: {
    display: "grid",
    gap: "12px",
    minWidth: "280px",
  },
  inputGroup: {
    display: "grid",
    gap: "8px",
  },
  label: {
    color: "#334155",
    fontWeight: 700,
    fontSize: "14px",
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #dbe2ea",
    padding: "12px 14px",
    fontSize: "15px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
  },
  primaryButton: {
    border: "none",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: 700,
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    fontWeight: 700,
  },
  panel: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    padding: "20px",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
    display: "grid",
    gap: "16px",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    flexWrap: "wrap",
  },
  panelActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  panelTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "20px",
  },
  panelText: {
    marginTop: "8px",
    marginBottom: 0,
    color: "#64748b",
    lineHeight: 1.55,
  },
  sliderRow: {
    display: "flex",
    gap: "14px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  slider: {
    flex: 1,
    minWidth: "200px",
  },
  sliderMeta: {
    color: "#0f172a",
    fontWeight: 700,
  },
  durationRow: {
    display: "grid",
    gap: "12px",
    gridTemplateColumns: "minmax(180px, 240px) minmax(0, 1fr)",
    alignItems: "end",
  },
  durationHint: {
    color: "#475569",
    lineHeight: 1.6,
  },
  notice: {
    borderRadius: "14px",
    padding: "14px 16px",
    fontWeight: 600,
  },
  noticeSuccess: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  noticeError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  noticeInfo: {
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
  },
  emptyState: {
    border: "1px dashed #cbd5e1",
    borderRadius: "16px",
    padding: "22px",
    color: "#64748b",
    backgroundColor: "#ffffff",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "16px",
  },
  card: {
    borderRadius: "16px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    padding: "18px",
    display: "grid",
    gap: "16px",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.04)",
  },
  qrWrap: {
    display: "flex",
    justifyContent: "center",
  },
  cardBody: {
    display: "grid",
    gap: "8px",
  },
  cardTitle: {
    color: "#0f172a",
    fontSize: "18px",
  },
  cardMeta: {
    color: "#64748b",
    fontSize: "13px",
  },
  accessBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "999px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: 700,
    marginTop: "6px",
  },
  accessBadgeEnabled: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  accessBadgeDisabled: {
    backgroundColor: "#fff7ed",
    border: "1px solid #fdba74",
    color: "#9a3412",
  },
  linkPreview: {
    color: "#2563eb",
    fontSize: "13px",
    lineHeight: 1.5,
    wordBreak: "break-all",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  linkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 12px",
    borderRadius: "10px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 700,
    textDecoration: "none",
  },
  secondaryMiniButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },
  dangerMiniButton: {
    border: "1px solid #fecaca",
    borderRadius: "10px",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },
};

export default QrMeza;
